import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, BookOpen, Loader2, Search, X } from 'lucide-react'
import clsx from 'clsx'

import { api } from '@/lib/api'
import { codigoVigente, useAyuda } from '@/lib/ayuda'
import { normalizar } from '@/lib/formato'

interface Articulo {
  codigo: string
  codigoPedido: string
  titulo: string
  html: string
}

interface ItemIndice {
  codigo: string
  titulo: string
  resumen: string | null

  /** El artículo sin etiquetas, para poder buscar en el cuerpo. */
  texto: string
}

// Cajón lateral de ayuda.
//
// Se abre con el botón «?» del encabezado y muestra el artículo de la pantalla
// que el usuario tiene adelante, sin sacarlo de donde estaba. Es la diferencia
// entre una ayuda que se usa y un manual que nadie abre: si para leer cómo se
// hace un arqueo hay que irse de la pantalla del arqueo, no se lee.
export function CajonAyuda() {
  const abierta = useAyuda((s) => s.abierta)
  const cerrar = useAyuda((s) => s.cerrar)
  const overrides = useAyuda((s) => s.overrides)
  const alternar = useAyuda((s) => s.alternar)

  // F1 abre la ayuda de la pantalla. Es la tecla que todo el mundo probó
  // alguna vez en un programa de Windows, y la caja se opera con teclado.
  //
  // El atajo vive ACÁ y no en el layout: así lo tiene cualquier pantalla que
  // muestre el cajón, y no solo las que cuelgan del layout del local. Estaba
  // en `Layout` y el panel de plataforma —que no lo usa— quedó sin F1, con el
  // botón de ayuda funcionando y la tecla no: la clase de diferencia que nadie
  // encuentra leyendo el código.
  //
  // Escucha en `window` porque el foco puede estar en cualquier campo, y por
  // eso mismo F1 está reservada: un medio de pago con esa tecla rápida dejaría
  // la ayuda inalcanzable justo en el diálogo de cobro.
  useEffect(() => {
    const alTecla = (e: KeyboardEvent) => {
      if (e.key !== 'F1') return

      e.preventDefault()
      alternar()
    }

    window.addEventListener('keydown', alTecla)
    return () => window.removeEventListener('keydown', alTecla)
  }, [alternar])

  const { slug } = useParams<{ slug: string }>()
  const ubicacion = useLocation()

  const codigoPantalla = codigoVigente(overrides, ubicacion.pathname, slug)

  // Cuál se está mostrando: la de la pantalla, o una que el usuario eligió del
  // índice. Se resetea al cerrar, así la próxima apertura vuelve a la de la
  // pantalla y no a la última que se leyó.
  const [elegido, setElegido] = useState<string | null>(null)
  const [viendoIndice, setViendoIndice] = useState(false)
  const [busqueda, setBusqueda] = useState('')

  const codigo = elegido ?? codigoPantalla

  // Cerrar vuelve el cajón a la ayuda de la pantalla: la próxima apertura no
  // tiene que arrancar en el último artículo que alguien leyó del índice.
  //
  // Va acá y no en un efecto que observa `abierta`: el efecto llamaba a tres
  // setState en su propio cuerpo, con un render de más en cada cierre, y
  // encima se disparaba también cuando el cajón se abría.
  //
  // Va con useCallback porque un efecto la usa como listener: sin eso, la
  // función cambia en cada render y el listener se da de baja y se vuelve a
  // registrar todo el tiempo.
  const cerrarCajon = useCallback(() => {
    setElegido(null)
    setViendoIndice(false)
    setBusqueda('')
    cerrar()
  }, [cerrar])

  // El cajón es un <dialog> nativo abierto con showModal, y no un div
  // posicionado.
  //
  // Es la única forma de que se vea cuando hay un modal abierto debajo: un
  // <dialog> modal vive en la capa superior del navegador, y cualquier div
  // hermano queda POR DEBAJO, invisible y sin poder recibir clics. La ayuda
  // del diálogo de cobro no se podría leer, que es justo cuando hace falta.
  //
  // De paso, el <dialog> trae gratis el Escape y el foco atrapado adentro.
  const ref = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    const dialogo = ref.current
    if (!dialogo) return

    if (abierta && !dialogo.open) dialogo.showModal()
    else if (!abierta && dialogo.open) dialogo.close()
  }, [abierta])

  useEffect(() => {
    const dialogo = ref.current
    if (!dialogo) return

    // cancel es el Escape. Se previene el cierre nativo para que el estado lo
    // maneje el store y no queden desincronizados.
    const alCancelar = (e: Event) => {
      e.preventDefault()
      cerrarCajon()
    }

    dialogo.addEventListener('cancel', alCancelar)
    return () => dialogo.removeEventListener('cancel', alCancelar)
  }, [cerrarCajon])

  const articulo = useQuery({
    queryKey: ['ayuda', codigo],
    queryFn: () => api.get<Articulo>(`/ayuda/articulo?codigo=${encodeURIComponent(codigo)}`, {
        destino: 'local',
      }),

    // Los artículos no cambian sin una actualización del sistema, así que no
    // hay motivo para volver a pedirlos en la misma sesión.
    staleTime: Infinity,
    enabled: abierta && !viendoIndice,
    retry: false,
  })

  const indice = useQuery({
    queryKey: ['ayuda', 'indice'],
    queryFn: () => api.get<ItemIndice[]>('/ayuda', { destino: 'local' }),
    staleTime: Infinity,
    enabled: abierta && viendoIndice,
  })

  const filtrado = useMemo(() => {
    const texto = normalizar(busqueda)
    if (!texto) return indice.data ?? []

    // Se busca también en el CUERPO del artículo, no solo en el título: quien
    // busca «arqueo» quiere llegar al artículo del turno, que se llama «Turno
    // de caja». Buscando solo por título, esa búsqueda no devuelve nada.
    //
    // Los que matchean por título van primero: si el artículo se llama como lo
    // que se buscó, es el que se está buscando.
    const coincide = (a: ItemIndice) =>
      normalizar(a.titulo).includes(texto) ||
      normalizar(a.resumen ?? '').includes(texto) ||
      normalizar(a.texto).includes(texto) ||
      a.codigo.includes(texto)

    return (indice.data ?? [])
      .filter(coincide)
      .sort(
        (a, b) =>
          Number(normalizar(b.titulo).includes(texto)) -
          Number(normalizar(a.titulo).includes(texto)),
      )
  }, [indice.data, busqueda])

  return (
    <dialog
      ref={ref}
      aria-label="Ayuda"
      className={clsx(
        // Pegado al borde derecho y a todo el alto. El margen en cero es
        // necesario: el <dialog> viene centrado con margin auto.
        // `hidden open:flex` y no `flex` pelado: la hoja de estilos del
        // navegador esconde un <dialog> sin el atributo open con un
        // display:none de MENOS especificidad que una clase de utilidad, así
        // que un `flex` suelto lo deja visible siempre, aunque nunca se haya
        // llamado a showModal. El cajón quedaba pegado a la derecha de todas
        // las pantallas.
        // `left-auto` es necesario: la hoja del navegador le pone left:0 y
        // right:0 al <dialog>, y con los dos puestos gana el izquierdo. Sin
        // esto el cajón se abre pegado al borde IZQUIERDO, tapando el menú.
        'fixed inset-y-0 right-0 left-auto m-0 hidden h-full max-h-none w-full max-w-[440px] flex-col open:flex',
        'border-l border-borde bg-papel p-0 shadow-elevada',

        // El fondo deja ver la pantalla de atrás a propósito: la ayuda habla
        // de lo que está ahí, y taparlo del todo obliga a cerrar para mirar.
        'backdrop:bg-tinta/20',
      )}
      onClick={(e) => {
        if (e.target === ref.current) cerrarCajon()
      }}
    >
      <div className="flex min-h-0 flex-1 flex-col">
        <header className="flex h-[58px] shrink-0 items-center gap-2 border-b border-borde px-4">
          {(elegido || viendoIndice) && (
            <button
              type="button"
              onClick={() => {
                if (viendoIndice) setViendoIndice(false)
                else setElegido(null)
              }}
              className="rounded p-1.5 text-tinta-suave hover:bg-crema-alt"
              aria-label="Volver"
            >
              <ArrowLeft className="h-[18px] w-[18px]" />
            </button>
          )}

          <span className="truncate text-[15px] font-bold text-tinta">
            {viendoIndice ? 'Temas de ayuda' : (articulo.data?.titulo ?? 'Ayuda')}
          </span>

          <div className="flex-1" />

          {!viendoIndice && (
            <button
              type="button"
              onClick={() => setViendoIndice(true)}
              className="rounded p-1.5 text-tinta-suave hover:bg-crema-alt"
              aria-label="Ver todos los temas"
              title="Ver todos los temas"
            >
              <BookOpen className="h-[18px] w-[18px]" />
            </button>
          )}

          <button
            type="button"
            onClick={cerrarCajon}
            className="rounded p-1.5 text-tinta-suave hover:bg-crema-alt"
            aria-label="Cerrar ayuda"
          >
            <X className="h-[18px] w-[18px]" />
          </button>
        </header>

        {viendoIndice ? (
          <>
            <div className="shrink-0 border-b border-borde p-3">
              <label className="relative block">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-tinta-tenue" />
                <input
                  className="campo pl-9"
                  placeholder="Buscar en la ayuda"
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                  autoFocus
                />
              </label>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto p-2">
              {indice.isLoading ? (
                <Cargando />
              ) : filtrado.length === 0 ? (
                <p className="px-3 py-6 text-center text-[14px] text-tinta-tenue">
                  No encontramos nada para «{busqueda}».
                </p>
              ) : (
                filtrado.map((a) => (
                  <button
                    key={a.codigo}
                    type="button"
                    onClick={() => {
                      setElegido(a.codigo)
                      setViendoIndice(false)
                    }}
                    className={clsx(
                      'block w-full rounded px-3 py-2.5 text-left transition-colors hover:bg-crema-alt',
                      a.codigo === codigoPantalla && 'bg-verde-tenue',
                    )}
                  >
                    <span className="block text-[14px] font-semibold text-tinta">{a.titulo}</span>
                    {a.resumen && (
                      <span className="mt-0.5 line-clamp-2 block text-[12.5px] leading-snug text-tinta-tenue">
                        {a.resumen}
                      </span>
                    )}
                  </button>
                ))
              )}
            </div>
          </>
        ) : (
          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">
            {articulo.isLoading ? (
              <Cargando />
            ) : articulo.isError ? (
              <div className="py-8 text-center">
                <BookOpen className="mx-auto mb-3 h-8 w-8 text-tinta-tenue" />
                <p className="text-[14px] font-semibold text-tinta">
                  Todavía no hay ayuda para esta pantalla
                </p>
                <p className="mt-1 text-[13px] text-tinta-tenue">
                  Podés buscar el tema en la lista completa.
                </p>
                <button
                  type="button"
                  onClick={() => setViendoIndice(true)}
                  className="btn-secundario btn-sm mt-4"
                >
                  Ver todos los temas
                </button>
              </div>
            ) : (
              <>
                {/* Cuando el artículo puntual de un modal no está escrito
                    todavía, el servidor devuelve el de la pantalla que lo
                    contiene. Se avisa, para que nadie lea la ayuda de la
                    grilla creyendo que es la del diálogo. */}
                {articulo.data && articulo.data.codigo !== articulo.data.codigoPedido && (
                  <p className="mb-4 rounded-lg border border-borde bg-crema-alt px-3 py-2 text-[12.5px] text-tinta-suave">
                    Esta es la ayuda general de la pantalla.
                  </p>
                )}

                {/* El HTML viene de los artículos incrustados en el propio
                    sistema, no de algo que cargue un usuario: es contenido
                    nuestro, versionado con el código. Si algún día la ayuda se
                    vuelve editable desde la UI, esto TIENE que pasar por un
                    sanitizador antes. */}
                <article
                  // La variante del cajón esconde el <h1> del artículo: el
                  // título ya está en el encabezado de al lado, y repetido dos
                  // veces se lee como un error.
                  className="ayuda ayuda-cajon"
                  dangerouslySetInnerHTML={{ __html: articulo.data?.html ?? '' }}
                />
              </>
            )}
          </div>
        )}
      </div>
    </dialog>
  )
}

function Cargando() {
  return (
    <div className="grid py-10 place-items-center">
      <Loader2 className="h-5 w-5 animate-spin text-verde" />
    </div>
  )
}
